/**
 * Progress service: student_resource_progress and student_session_progress.
 */

import { supabase } from '../lib/supabase'
import {
  handleQuery,
  formatSupabaseError,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'

/**
 * Get all resource completion for a student in an event.
 * @param {string} studentId
 * @param {string} eventId
 * @param {{ role?: string, userId?: string }} [options]
 */
export const getResourceProgress = async (studentId, eventId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only view your own progress.')
  }

  const { data, error } = await supabase
    .from('student_resource_progress')
    .select('*')
    .eq('student_id', studentId)
    .eq('event_id', eventId)

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to load progress.') }
  return { data: data ?? [], error: null }
}

/**
 * Mark a resource complete or incomplete (toggle). Upserts student_resource_progress.
 * @param {string} studentId
 * @param {string} resourceId
 * @param {string} eventId
 * @param {string} completedBy - user id (student, agent, or admin)
 * @param {boolean} isCompleted
 * @param {{ role?: string, userId?: string }} [options]
 */
export const markResourceComplete = async (
  studentId,
  resourceId,
  eventId,
  completedBy,
  isCompleted,
  options = {}
) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only update your own progress.')
  }

  if (roleContext.role === 'agent') {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) return { data: null, error: access.error }
  }

  const { data: existing } = await supabase
    .from('student_resource_progress')
    .select('id')
    .eq('student_id', studentId)
    .eq('resource_id', resourceId)
    .maybeSingle()

  const payload = {
    student_id: studentId,
    event_id: eventId,
    resource_id: resourceId,
    is_completed: isCompleted,
    completed_at: isCompleted ? new Date().toISOString() : null,
    completed_by: isCompleted ? completedBy : null
  }

  if (existing) {
    const { data, error } = await supabase
      .from('student_resource_progress')
      .update(payload)
      .eq('id', existing.id)
      .select('*')
      .single()
    if (error) return { data: null, error: formatSupabaseError(error, 'Unable to update progress.') }
    return { data, error: null }
  }

  const { data, error } = await supabase
    .from('student_resource_progress')
    .insert(payload)
    .select('*')
    .single()

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to save progress.') }
  return { data, error: null }
}

/**
 * Get session progress for a student in an event.
 */
export const getSessionProgress = async (studentId, eventId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  if (roleContext.role === 'student' && roleContext.userId !== studentId) {
    return accessDeniedResponse('You can only view your own progress.')
  }

  const { data, error } = await supabase
    .from('student_session_progress')
    .select('*')
    .eq('student_id', studentId)
    .eq('event_id', eventId)

  if (error) return { data: null, error: formatSupabaseError(error, 'Unable to load session progress.') }
  return { data: data ?? [], error: null }
}

/**
 * Calculate overall event progress: (completed resources / total resources) * 100.
 * @param {string} studentId
 * @param {string} eventId
 * @param {{ resourceCount?: number }} [opts] - total resource count (if already fetched)
 */
export const calculateEventProgress = async (studentId, eventId, options = {}) => {
  const roleContext = await resolveRoleContext(options)
  if (roleContext.error) return { data: null, error: roleContext.error }

  const { data: progress, error: progressError } = await getResourceProgress(
    studentId,
    eventId,
    options
  )
  if (progressError) return { data: null, error: progressError }

  const { count: total, error: countError } = await supabase
    .from('event_resources')
    .select('*', { count: 'exact', head: true })
    .eq('event_id', eventId)
  const totalResources = countError ? 0 : (total ?? 0)
  const completed = (progress ?? []).filter((p) => p.is_completed).length
  const percentage = totalResources > 0 ? Math.round((completed / totalResources) * 100) : 0

  return {
    data: {
      completedResources: completed,
      totalResources: totalResources,
      percentage
    },
    error: null
  }
}
